/* Stat / StatGrid — cartão de KPI (label + valor grande + ícone opcional + hint/trend) e grid
   responsivo de KPIs. Usado no dashboard do publisher e no analytics. shadcn tokens + cn(). */
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/utils.js';

const statTrendVariants = cva('font-medium text-xs', {
  variants: {
    trend: {
      up: 'text-success',
      down: 'text-destructive',
      flat: 'text-muted-foreground',
    },
  },
  defaultVariants: { trend: 'flat' },
});

export interface StatProps extends ComponentProps<'div'>, VariantProps<typeof statTrendVariants> {
  readonly label: ReactNode;
  readonly value: ReactNode;
  readonly icon?: ReactNode;
  /** Texto auxiliar sob o valor (ex.: "vs. 30d", "3 ativos"). */
  readonly hint?: ReactNode;
  /** Valor de tendência exibido como chip (ex.: "+12%"); cor via `trend`. */
  readonly change?: ReactNode;
}

export function Stat({ label, value, icon, hint, change, trend, className, ...props }: StatProps) {
  return (
    <div
      data-slot="stat"
      className={cn(
        'flex flex-col gap-1 rounded-md border border-border bg-card p-4 text-card-foreground',
        className,
      )}
      {...props}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground text-sm">{label}</span>
        {icon !== undefined && <span className="text-muted-foreground [&_svg]:size-4">{icon}</span>}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-semibold text-2xl text-foreground tabular-nums">{value}</span>
        {change !== undefined && <span className={cn(statTrendVariants({ trend }))}>{change}</span>}
      </div>
      {hint !== undefined && <span className="text-muted-foreground text-xs">{hint}</span>}
    </div>
  );
}

/** Grid responsivo de KPIs (1 col mobile → N col ≥sm). `columns` controla o teto de colunas. */
export function StatGrid({
  columns = 3,
  className,
  ...props
}: ComponentProps<'div'> & { readonly columns?: 2 | 3 | 4 }) {
  const cols = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4',
  } as const;
  return (
    <div
      data-slot="stat-grid"
      className={cn('grid grid-cols-1 gap-3', cols[columns], className)}
      {...props}
    />
  );
}
